import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Download, Loader2, CheckCircle2, Building2, Printer, Lock, Unlock, AlertCircle } from "lucide-react";
import { generatePostcardPdf } from "@/utils/postcardPdfGenerator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

function buildQueryBusiness(selectFields: string) {
  return supabase
    .from("mailing_records")
    .select(selectFields)
    .eq("times_mailed", 0)
    .not("business_name", "is", null)
    .neq("business_name", "")
    .eq("mailing_status", "active")
    .order("business_name", { ascending: true })
    .order("last_name", { ascending: true });
}

export function BusinessMailingExport() {
  const [batchRecords, setBatchRecords] = useState<any[] | null>(null);
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [batchLimit, setBatchLimit] = useState<number>(100);
  const [preparingBatch, setPreparingBatch] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [generatingCards, setGeneratingCards] = useState(false);
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [mailDate, setMailDate] = useState(new Date().toISOString().split("T")[0]);
  const [committing, setCommitting] = useState(false);

  const handlePrepareBatch = async () => {
    setPreparingBatch(true);
    try {
      let allRecords: any[] = [];
      let from = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await buildQueryBusiness(
          "id, seller_id, prefix, first_name, last_name, business_name, recorded_date, street_address, city, state, zip_code, county, document_type, times_mailed"
        ).range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No business records found matching criteria");
        setPreparingBatch(false);
        return;
      }

      // Apply limit
      if (batchLimit && batchLimit > 0 && batchLimit < allRecords.length) {
        allRecords = allRecords.slice(0, batchLimit);
      }

      setBatchRecords(allRecords);
      setBatchIds(allRecords.map((r) => r.id));
      toast.success(`Batch locked: ${allRecords.length} records ready for postcards, export & commit`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to prepare batch");
    } finally {
      setPreparingBatch(false);
    }
  };

  const handleReleaseBatch = () => {
    setBatchRecords(null);
    setBatchIds([]);
    toast.info("Batch released");
  };

  const handleExport = async () => {
    if (!batchRecords) return;
    setExporting(true);
    try {
      const rows = batchRecords.map((r) => {
        const prefix = r.prefix && r.prefix !== "explicitly_none" ? r.prefix : "";
        return {
          "Seller ID": r.seller_id || "",
          "Prefix": prefix,
          "First Name": r.first_name || "",
          "Last Name": r.last_name || "",
          "Business Name": r.business_name || "",
          "Filing Date": r.recorded_date || "",
          "Street Address": r.street_address || "",
          "City": r.city || "",
          "State": (r.state || "").toUpperCase(),
          "Zip Code": (r.zip_code || "").split("-")[0],
          "County": r.county || "",
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 20 }, { wch: 10 }, { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 14 }, { wch: 30 },
        { wch: 18 }, { wch: 8 }, { wch: 12 }, { wch: 16 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Business Records");
      XLSX.writeFile(wb, `Business_Batch_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${rows.length} business records to Excel`);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleGeneratePostcards = () => {
    if (!batchRecords) return;
    setGeneratingCards(true);
    try {
      const postcardRecords = batchRecords.map((r) => ({
        documentType: r.document_type || "Land Contract",
        county: r.county ? `${r.county} County` : "",
        businessName: r.business_name || "",
        recordedDate: r.recorded_date
          ? new Date(r.recorded_date).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
          : "",
      }));

      generatePostcardPdf(postcardRecords);
      toast.success(`Generated ${postcardRecords.length} postcards`);
    } catch (err) {
      console.error(err);
      toast.error("Postcard generation failed");
    } finally {
      setGeneratingCards(false);
    }
  };

  const handleCommitToMail = async () => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    if (batchIds.length === 0) return;
    setCommitting(true);
    try {
      const batchSize = 500;
      for (let i = 0; i < batchIds.length; i += batchSize) {
        const batch = batchIds.slice(i, i + batchSize);

        const historyEntries = batch.map((id) => ({
          mailing_record_id: id,
          campaign_name: campaignName.trim(),
          mailed_date: mailDate,
          mailing_round: 1,
        }));

        const { error: historyError } = await supabase
          .from("mailing_history")
          .insert(historyEntries);
        if (historyError) throw historyError;

        for (const id of batch) {
          const { error: updateError } = await supabase
            .from("mailing_records")
            .update({
              times_mailed: (await supabase.from("mailing_records").select("times_mailed").eq("id", id).single()).data?.times_mailed + 1 || 1,
              last_mailed_date: mailDate,
            })
            .eq("id", id);
          if (updateError) throw updateError;
        }
      }

      toast.success(`Committed ${batchIds.length} business records to "${campaignName.trim()}"`);
      setCommitDialogOpen(false);
      setCampaignName("");
      // Release batch after commit since these records are no longer "never mailed"
      setBatchRecords(null);
      setBatchIds([]);
    } catch (err) {
      console.error(err);
      toast.error("Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const isBatchLocked = batchRecords !== null;

  return (
    <>
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Business Entities — Never Mailed
          </CardTitle>
          <CardDescription>
            Prepare a batch to lock in the same records across postcards, Excel export, and commit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1: Prepare Batch */}
          {!isBatchLocked ? (
            <div className="flex gap-3 items-end flex-wrap">
              <div>
                <Label>Batch Size</Label>
                <Input
                  type="number"
                  min={1}
                  value={batchLimit}
                  onChange={(e) => setBatchLimit(parseInt(e.target.value) || 100)}
                  className="w-32"
                />
              </div>
              <Button onClick={handlePrepareBatch} disabled={preparingBatch} className="gap-2">
                {preparingBatch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                {preparingBatch ? "Loading..." : "Prepare Batch"}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="secondary" className="text-sm px-3 py-1 gap-1.5">
                  <Lock className="h-3.5 w-3.5" />
                  {batchIds.length} records locked
                </Badge>
                <Button variant="ghost" size="sm" onClick={handleReleaseBatch} className="gap-1.5 text-muted-foreground">
                  <Unlock className="h-3.5 w-3.5" />
                  Release Batch
                </Button>
              </div>

              {/* Step 2: Actions on locked batch */}
              <div className="flex gap-3 items-center flex-wrap">
                <Button variant="outline" onClick={handleGeneratePostcards} disabled={generatingCards} className="gap-2">
                  {generatingCards ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                  {generatingCards ? "Generating..." : "Print Postcards"}
                </Button>
                <Button variant="outline" onClick={handleExport} disabled={exporting} className="gap-2">
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {exporting ? "Exporting..." : "Download Excel"}
                </Button>
                <Button onClick={() => setCommitDialogOpen(true)} className="gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Commit to Mail
                </Button>
              </div>

              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                All 3 actions target the exact same {batchIds.length} records. Commit last — it releases the batch.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={commitDialogOpen} onOpenChange={setCommitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit {batchIds.length} Business Records to Mail</DialogTitle>
            <DialogDescription>
              This will mark the locked batch as mailed, increment their mail count, and create history entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. April 2026 – Business Postcards"
              />
            </div>
            <div>
              <Label>Mail Date</Label>
              <Input
                type="date"
                value={mailDate}
                onChange={(e) => setMailDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCommitToMail} disabled={committing} className="gap-2">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {committing ? "Committing..." : "Commit Records"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
